import React, { useState, useEffect } from 'react';
import { autorun, reaction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Checkbox, IconButton, Link, Icon } from '@fluentui/react';
import { mergeStyleSets, FontSizes, FontWeights } from '@fluentui/react';
import { useHistory } from 'react-router-dom';

import RichList, { ItemAction } from 'src/components/base/RichList';
import { formatActionType } from 'src/components/Scheduler/actionList';
import { getDocumentName } from 'shared/services/scheduler';

import { FlowstepCategory } from 'shared/utils/flowstep';
import { runShortAction } from 'src/services/utils';
import { omit, pick } from 'shared/utils/utils';
import { toQueryString } from 'shared/utils/utils';

const styles = mergeStyleSets({
  root: {
    margin: '12px',
  },
  document: {
    fontSize: FontSizes.medium,
    // fontWeight: FontWeights.semibold,
    color: '#333',

    selectors: {
      '& > span': {
        marginLeft: '8px',
      },
    }
  },

  action: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // width: '100%',

    height: '60px',
    padding: '0px 16px 0px 36px',

    selectors: {
      ':hover': {
        backgroundColor: '#eaeaea',
        '> button:last-child': {
          display: 'inline-block',
        }
      },
      '> button:last-child': {
        display: 'none',
      }
    }
  },
  actionDescription: {
    flex: '1 1 auto',
    marginLeft: '8px',
    selectors: {
      '&>.title': {
        fontSize: FontSizes.medium,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      },
      '&>.subtitle': {
        fontSize: FontSizes.small,
        color: '#666',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
      },
    },
  },
  buttonsBlock: {
    display: 'flex',
    flexFlow: 'row',
    justifyContent: 'space-between',
    margin: '10px 0 0 -16px',

    selectors: {
      '& > *': {
        flexGrow: 1,
        flexBasis: 0,
        marginLeft: '16px',
      },
    },
  },

  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  emptyStateIcon: {
    fontSize: '48px',
    color: '#a19f9d',
    marginBottom: '16px',
  },
  emptyStateTitle: {
    fontSize: FontSizes.large,
    fontWeight: FontWeights.semibold,
    color: '#323130',
    marginBottom: '8px',
    lineHeight: '1.4',
  },
  emptyStateDescription: {
    fontSize: FontSizes.medium,
    color: '#605e5c',
    lineHeight: '1.5',
    maxWidth: '400px',
    marginBottom: '20px',
  },
  emptyStateLink: {
    color: '#0078d4',
    cursor: 'pointer',
    textDecoration: 'none',
    fontWeight: FontWeights.semibold,
    selectors: {
      ':hover': {
        textDecoration: 'underline',
        color: '#106ebe',
      },
    },
  },
});


const Schedules = ({ dataStore, pageControl }) => {
  const [items, setItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [modifiedItems, setModifiedItems] = useState([]);
  const { setHeaderCommands: setPageCommands, setDirtyActions } = pageControl;
  const history = useHistory();

  const resetChanges = async () => runShortAction(async () => {
    const allUserActions = await dataStore.fetchAllUserActions();
    const { scheduler: { document_id: thisDocumentId } } = dataStore;

    // const links = await fetchDocumentLinks({ document_ids: allUserActions.map(x => x.document_id) }, userStore.token);

    const items = allUserActions
      .map((document, index) => {
        return {
          ...omit(document, ['actions']), name: getDocumentName(document.document_path),
          link: `${config.api.serverUrl}/scheduler-open-document?${toQueryString({ document_id: document.document_id })}`,
          isThisDocument: false,
          isCollapsed: true, children: document.actions.map(action => {
            const { flow_id } = action;
            const flow = (document.flows ?? []).find(x => x.id === flow_id);
            return { ...action, flow, document: pick(document, ['id', 'document_id']) };
          })
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' }));

    const thisIndex = items.findIndex(x => x.document_id === thisDocumentId);
    if (thisIndex !== -1) {
      Object.assign(items[thisIndex], { isThisDocument: true, isCollapsed: false });
      items.splice(0, 0, ...items.splice(thisIndex, 1));
    }

    setItems(items);
    setModifiedItems([]);
    setDirtyActions(null);
    setIsLoaded(true);
  });

  useEffect(() => {
    resetChanges();
    return reaction(
      () => ({ areActual: dataStore.areAllUserActionsActual }),
      ({ areActual }) => !areActual && resetChanges()
    )
  }, []);

  useEffect(() => {
    const disabled = !modifiedItems.length;
    const pageCommands = [
      { key: 'cancel', disabled, iconProps: { iconName: 'Clear' }, onClick: handleResetClick },
      { key: 'apply', disabled, iconProps: { iconName: 'Accept' }, onClick: handleApplyClick }
    ];
    setPageCommands(pageCommands);
    modifiedItems.length && setDirtyActions({
      text: 'You have unsaved changes in your actions',
      onSave: handleApplyClick,
    });
  }, [modifiedItems]);

  const handleResetClick = () => {
    resetChanges();
  }

  const handleApplyClick = async () => {
    await runShortAction(async () => {
      const toDelete = modifiedItems
        .filter(x => x.action === ItemAction.Delete)
        .map(({ item }) => ({ id: item.id, document_id: item.document.document_id }));
      const toUpdate = modifiedItems
        .filter(x => x.action === ItemAction.Update)
        .map(({ item }) => ({ action: omit(item, ['document']), document_id: item.document.document_id }));

      !!toDelete.length && await dataStore.deleteActions(toDelete);
      !!toUpdate.length && await dataStore.updateActions(toUpdate);

      dataStore.clearAllUserActions();
    });
  };

  const handleItemsChanged = (items, modifiedItems) => {
    setItems(items);
    setModifiedItems(modifiedItems);
  }

  const handleItemsOrderChanged = (parent, item) => {
  }

  const handleCollapseChanged = (items) => {
    setItems(items);
  }

  const handleCreateAutomation = () => {
    history.push('/home/scheduler');
  };

  const handleRenderItem = (item, onItemChanged, onItemRemoved) => {
    return (<Action item={item} onItemRemoved={onItemRemoved} onItemChanged={onItemChanged} />)
  }

  const handleRenderGroup = (workbook) => {
    return (
      <div className={styles.document}>
        <Link href={workbook.link} target={'_blank'}>{workbook.name}</Link>
        {/* <IconButton key="download" iconProps={{ iconName: 'Download' }} onClick={() => window.open(workbook.document_path, '_blank') } />
        { workbook.isThisDocument && <span>{'(This workbook)'}</span>} */}
      </div>
    );
  };

  return (
    <div className={styles.root}>
      {isLoaded &&
        (
          items.length
            ?
            <RichList
              editMode={false}
              getParentId={(item) => item.document.id}
              items={items}
              modifiedItems={modifiedItems}
              onItemsChanged={handleItemsChanged}
              onItemsOrderChanged={handleItemsOrderChanged}
              onRenderItem={handleRenderItem}
              groupHeaderProps={{
                onRenderTitle: handleRenderGroup,
                onCollapseChanged: handleCollapseChanged
              }}
            />
            :
            (
              <div className={styles.emptyState}>
                <Icon iconName="Calendar" className={styles.emptyStateIcon} />
                <div className={styles.emptyStateTitle}>
                  No Automated Schedules Found
                </div>
                <div className={styles.emptyStateDescription}>
                  You haven't set up any automatic schedules yet.{' '}
                  <span
                    className={styles.emptyStateLink}
                    onClick={handleCreateAutomation}
                  >
                    Create an automation
                  </span>{' '}
                  to run data operations on a schedule in the current workbook.
                </div>
              </div>
            ))
      }




    </div>
  );
}

const Action = ({ item, onItemRemoved, onItemChanged }) => {
  const { action, is_active, schedule, flow } = item;

  const handleItemActivation = (is_active) => {
    onItemChanged({ ...item, is_active });
  };

  const handleItemRemoved = () => {
    onItemRemoved(item);
  }

  return (
    <div className={styles.action}>
      <Checkbox
        checked={is_active}
        onChange={(event, value) => handleItemActivation(value)}
      />
      <div className={styles.actionDescription}>
        {/* <div >Id: {item.id}</div> */}
        <div className='title'>{flow
          ? flow.name
          : action === FlowstepCategory.Pull ? 'Pull Data' : 'Push Data'}
        </div>
        <div className='subtitle'>{formatActionType(schedule)}</div>
      </div>
      <IconButton iconProps={{ iconName: 'Delete' }} onClick={handleItemRemoved} />
    </div>
  );
}

export default inject('dataStore')(observer(Schedules));